import {useCallback, useEffect, useState} from 'react';
import {Alert, BackHandler} from 'react-native';
import analytics from '@react-native-firebase/analytics';
import {api} from '../../../../../services/api';
import {PedidoDTO} from '../../../../../dtos/PedidoDTO';
import {ChargePixFn, PixGatewayCallbacks} from './types';

export type PixBank = {
  id: {$oid: string};
  nome: string;
  image_url: string;
};

type Options = PixGatewayCallbacks & {
  chargePix: ChargePixFn;
  cart: PedidoDTO[];
  isActive: boolean;
  pedidoValue: number;
  couponCode?: string;
};

export function usePixGateway({
  chargePix,
  cart,
  isActive,
  pedidoValue,
  couponCode,
  onSuccess,
  onError,
}: Options) {
  const [isPaying, setIsPaying] = useState(false);
  const [taxaPix, setTaxaPix] = useState(0);
  const [cashback, setCashback] = useState(0);
  const [banks, setBanks] = useState<PixBank[]>([]);
  const [showBankAlert, setShowBankAlert] = useState(false);
  const [isLoadingTax, setIsLoadingTax] = useState(false);
  const [isLoadingCashback, setIsLoadingCashback] = useState(false);
  const [isLoadingBanks, setIsLoadingBanks] = useState(false);

  const fetchTax = useCallback(async () => {
    if (cart && cart.length > 0) {
      setIsLoadingTax(true);
      try {
        const res = await api.post('/taxaServicoPix', {produtos: cart});
        setTaxaPix(res.data);
      } finally {
        setIsLoadingTax(false);
      }
    }
  }, []);

  const fetchCashback = useCallback(async () => {
    if (cart && cart.length > 0) {
      setIsLoadingCashback(true);
      try {
        const res = await api.post('/consultaBaratinhasCompra', {produtos: cart});
        setCashback(res.data);
      } finally {
        setIsLoadingCashback(false);
      }
    }
  }, []);

  const fetchBanks = useCallback(async () => {
    setIsLoadingBanks(true);
    try {
      const res = await api.get('/consultaBancos');
      if (res.data) {
        setBanks(res.data);
      }
    } catch {
      Alert.alert(
        'Ocorreu um problema',
        'Ocorreu um problema ao listar os bancos disponíveis, tente novamente mais tarde.',
      );
    } finally {
      setIsLoadingBanks(false);
    }
  }, []);

  useEffect(() => {
    if (isActive) {
      fetchTax();
      fetchCashback();
      fetchBanks();
    }
  }, [isActive, fetchTax, fetchCashback, fetchBanks]);

  const pay = useCallback(
    async (bank?: string) => {
      setIsPaying(true);
      setShowBankAlert(false);
      BackHandler.addEventListener('hardwareBackPress', () => true);

      try {
        const response = await chargePix({banco: bank});

        // Depois do chargePix, não antes: senão toda tentativa abandonada conta como
        // conversão. Ainda é só "código gerado", não pagamento — por isso sem receita.
        analytics().logEvent('purchase_pix', {
          value: pedidoValue,
          currency: 'BRL',
          coupon: couponCode,
        });

        onSuccess(response.code);
      } catch (err: any) {
        const data = err?.response?.data;
        onError(data?.msg || data?.error);
      } finally {
        setIsPaying(false);
      }
    },
    [chargePix, pedidoValue, couponCode, onSuccess, onError],
  );

  const handleRedirect = useCallback(() => {
    if (banks.length === 1) {
      pay(banks[0].nome);
      return;
    }
    setShowBankAlert(true);
  }, [banks, pay]);

  return {
    pay,
    isPaying,
    taxaPix,
    cashback,
    banks,
    showBankAlert,
    setShowBankAlert,
    isLoadingTax,
    isLoadingCashback,
    isLoadingBanks,
    handleRedirect,
  };
}
